import { useEffect, useRef, useState } from 'react'
import {
  FullscreenIconButton,
  FullscreenShell,
  useFullscreen,
} from './Fullscreen'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string
        alt?: string
        'camera-controls'?: boolean | string
        'auto-rotate'?: boolean | string
        'shadow-intensity'?: string | number
        'environment-image'?: string
        ar?: boolean | string
      }
    }
  }
}

let scriptInjected = false

function ensureScript() {
  if (scriptInjected) return
  scriptInjected = true
  const s = document.createElement('script')
  s.type = 'module'
  // Served from web/public/ so the demo works fully offline. To upgrade:
  // curl -L -o web/public/model-viewer.min.js https://ajax.googleapis.com/ajax/libs/model-viewer/<ver>/model-viewer.min.js
  s.src = '/model-viewer.min.js'
  document.head.appendChild(s)
}

interface Props {
  src: string
  empty?: boolean
  // When the GLB contains more than the lesion (e.g. a translucent breast
  // envelope), getDimensions() returns the bounding box of everything. Pass
  // the lesion's own bbox here so we still display lesion-only dimensions.
  lesionDimsMm?: [number, number, number] | null
}

interface Dim {
  x: number
  y: number
  z: number
}

export function MeshViewer({ src, empty, lesionDimsMm }: Props) {
  const ref = useRef<HTMLElement | null>(null)
  const [dim, setDim] = useState<Dim | null>(null)
  const [fs, , closeFs, toggleFs] = useFullscreen()

  useEffect(() => {
    ensureScript()
  }, [])

  // Read bounding box once the model is loaded.
  // glTF unit is meters by spec; our build_demo_bundle.py exports vertices in
  // mm (physical coordinates from voxel × spacing). model-viewer's
  // getDimensions() returns the raw scene-unit numbers — for us those are mm.
  useEffect(() => {
    if (empty || lesionDimsMm) return
    const el = ref.current as (HTMLElement & {
      getDimensions?: () => { x: number; y: number; z: number }
      loaded?: boolean
    }) | null
    if (!el) return
    let mounted = true
    setDim(null)
    const apply = () => {
      if (!mounted) return
      try {
        const d = el.getDimensions?.()
        if (d) setDim({ x: d.x, y: d.y, z: d.z })
      } catch {
        /* ignore — element not ready */
      }
    }
    el.addEventListener('load', apply)
    if (el.loaded) apply()
    return () => {
      mounted = false
      el.removeEventListener('load', apply)
    }
  }, [src, empty, lesionDimsMm])

  const shownDim: Dim | null = lesionDimsMm
    ? { x: lesionDimsMm[0], y: lesionDimsMm[1], z: lesionDimsMm[2] }
    : dim

  if (empty) {
    return (
      <div className="aspect-square rounded border border-warn/40 bg-warn/5 flex items-center justify-center p-4 text-center">
        <div>
          <div className="text-warn text-sm font-medium mb-1">3 boyutlu model üretilemedi</div>
          <div className="text-text-dim text-xs">
            Bu vaka için segmentasyon adımı lezyonu tamamen kaçırdı. Sınıflandırıcı
            tahmini bir merkezden yine çalıştırılmıştır, ancak 3 boyutlu yüzey
            oluşturulamadı. Bu durum modelin bilinen sınırlamalarındandır.
          </div>
        </div>
      </div>
    )
  }

  const sizeCls = fs
    ? 'aspect-square w-auto mx-auto h-[min(78vh,78vw)] bg-viewer3d relative'
    : 'aspect-square rounded overflow-hidden bg-viewer3d relative'

  return (
    <FullscreenShell
      expanded={fs}
      onClose={closeFs}
      title="3 boyutlu lezyon ve göğüs modeli"
    >
      <div className={sizeCls}>
        {/* @ts-expect-error custom element */}
        <model-viewer
          ref={ref as never}
          src={src}
          alt="3B lezyon ve göğüs modeli"
          camera-controls
          auto-rotate
          shadow-intensity="1"
          style={{ width: '100%', height: '100%', backgroundColor: 'var(--color-viewer3d)' }}
        />
        {shownDim && (
          <div className="absolute bottom-1 left-1 pointer-events-none">
            <div className="bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-[10px] sm:text-xs font-mono text-white/85">
              lezyon {shownDim.x.toFixed(1)} × {shownDim.y.toFixed(1)} × {shownDim.z.toFixed(1)} mm
            </div>
          </div>
        )}
        <div className="absolute top-1 right-12 pointer-events-none">
          <div className="bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-[10px] font-mono text-white/85 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#d92e2e' }} />
            <span>lezyon</span>
            <span className="inline-block w-2 h-2 rounded-full ml-1" style={{ backgroundColor: '#3385f0' }} />
            <span>göğüs</span>
          </div>
        </div>
        <FullscreenIconButton expanded={fs} onToggle={toggleFs} />
      </div>
    </FullscreenShell>
  )
}
